import React, { useState, useEffect } from 'react';
import { getSessionHistory, getPaymentHistory } from '../services/api';
import './HistoryScreen.css';

export default function HistoryScreen({ userId, onBack }) {
  const [tab, setTab]               = useState('interviews'); // interviews | payments
  const [sessions, setSessions]     = useState([]);
  const [payments, setPayments]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState(null); // expanded session id

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      getSessionHistory(userId),
      getPaymentHistory(userId),
    ]).then(([s, p]) => {
      setSessions(s.data || []);
      setPayments(p.data || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  const gradeColor = (grade) => {
    if (!grade) return '#8ba3d4';
    if (grade === 'A') return '#34d399';
    if (grade === 'B') return '#6394ff';
    if (grade === 'C') return '#fbbf24';
    return '#f87171';
  };

  const recColor = (rec) => {
    if (!rec) return '#8ba3d4';
    if (rec === 'Hire') return '#34d399';
    if (rec === 'Maybe') return '#fbbf24';
    return '#f87171';
  };

  const formatDate = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDuration = (secs) => {
    if (!secs) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  const formatAmount = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

  return (
    <div className="history-root">
      <div className="history-bg">
        <div className="orb orb-1" /><div className="orb orb-2" />
        <div className="grid-lines" />
      </div>

      <div className="history-container">

        {/* Header */}
        <div className="history-header">
          <button className="back-btn" onClick={onBack}>← Back</button>
          <h2>My <span className="gradient-text">Dashboard</span></h2>
          <div /> {/* spacer */}
        </div>

        {/* Stats row */}
        <div className="stats-row">
          <div className="stat-card">
            <span className="stat-num">{sessions.length}</span>
            <span className="stat-label">Total Interviews</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">
              {sessions.filter(s => s.status === 'COMPLETED').length}
            </span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">
              {sessions.filter(s => s.overallScore != null).length > 0
                ? (sessions.filter(s => s.overallScore != null)
                    .reduce((sum, s) => sum + s.overallScore, 0) /
                   sessions.filter(s => s.overallScore != null).length
                  ).toFixed(1)
                : '—'
              }
            </span>
            <span className="stat-label">Avg Score</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">
              {sessions.filter(s => s.recommendation === 'Hire').length}
            </span>
            <span className="stat-label">Hire Results</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">{payments.length}</span>
            <span className="stat-label">Payments</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="history-tabs">
          <button
            className={`htab ${tab === 'interviews' ? 'active' : ''}`}
            onClick={() => setTab('interviews')}>
            🎙️ Interviews
          </button>
          <button
            className={`htab ${tab === 'payments' ? 'active' : ''}`}
            onClick={() => setTab('payments')}>
            💳 Payments
          </button>
        </div>

        {loading && (
          <div className="history-loading">
            <div className="spinner-lg" />
            <p>Loading your history...</p>
          </div>
        )}

        {/* ── Interviews tab ── */}
        {!loading && tab === 'interviews' && (
          <div className="history-list">
            {sessions.length === 0 && (
              <div className="empty-state">
                <span>🎙️</span>
                <p>No interviews yet. Start your first interview!</p>
              </div>
            )}
            {sessions.map(s => (
              <div key={s.sessionId} className="session-card">
                <div
                  className="session-card-header"
                  onClick={() => setExpanded(expanded === s.sessionId ? null : s.sessionId)}>

                  <div className="session-meta">
                    <span className="session-role">{s.jobRole}</span>
                    <span className="session-stack">{s.techStack}</span>
                    <span className="session-exp">{s.experienceLevel}</span>
                  </div>

                  <div className="session-badges">
                    {s.grade && (
                      <span className="badge" style={{ color: gradeColor(s.grade), borderColor: gradeColor(s.grade) }}>
                        Grade {s.grade}
                      </span>
                    )}
                    {s.overallScore != null && (
                      <span className="badge badge-score">
                        {s.overallScore}/10
                      </span>
                    )}
                    {s.recommendation && (
                      <span className="badge" style={{ color: recColor(s.recommendation), borderColor: recColor(s.recommendation) }}>
                        {s.recommendation}
                      </span>
                    )}
                    <span className={`status-pill status-${s.status?.toLowerCase()}`}>
                      {s.status}
                    </span>
                  </div>

                  <div className="session-date-row">
                    <span className="session-date">{formatDate(s.startedAt)}</span>
                    <span className="session-dur">{formatDuration(s.actualDurationSeconds)}</span>
                    <span className="session-qs">{s.totalQuestionsAsked} Qs</span>
                    <span className="expand-icon">{expanded === s.sessionId ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded: evaluation summary */}
                {expanded === s.sessionId && (
                  <div className="session-expanded">
                    {s.evaluationSummary ? (
                      <>
                        <h4>📊 Evaluation Summary</h4>
                        <p className="eval-text">{s.evaluationSummary}</p>
                      </>
                    ) : (
                      <p className="no-eval">No evaluation available for this session.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Payments tab ── */}
        {!loading && tab === 'payments' && (
          <div className="history-list">
            {payments.length === 0 && (
              <div className="empty-state">
                <span>💳</span>
                <p>No payment history yet.</p>
              </div>
            )}
            {payments.map(p => (
              <div key={p.paymentId} className="payment-card">
                <div className="payment-left">
                  <span className="payment-plan">
                    {p.plan === 'yearly' ? '🌟 Yearly Plan' : '📅 Monthly Plan'}
                  </span>
                  <span className="payment-id">ID: {p.gatewayPaymentId || p.paymentId}</span>
                  <span className="payment-date">{formatDate(p.createdAt)}</span>
                </div>
                <div className="payment-right">
                  <span className="payment-amount">{formatAmount(p.amount)}</span>
                  <span className={`payment-status ps-${p.status?.toLowerCase()}`}>
                    {p.status}
                  </span>
                  {p.subscriptionEnd && (
                    <span className="payment-expiry">
                      Valid till {formatDate(p.subscriptionEnd)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}